// Kartu QR ulasan Google untuk dicetak per cabang (standing acrylic / stiker / kartu): logo cabang, ajakan memberi ulasan,
// bintang, dan QR ke halaman tulis ulasan. Dibuat sebagai SVG berukuran milimeter supaya tajam di semua ukuran cetak.
import { svgQr } from './qrStruk';

/** Ukuran pilihan (mm). 'custom' memakai lebar x tinggi yang diketik pengguna (cm). */
export const UKURAN_KARTU = [
  { kunci: 'kotak10', nama: 'Kotak 10 × 10 cm (1:1)', lebar: 100, tinggi: 100 },
  { kunci: 'kotak15', nama: 'Kotak 15 × 15 cm (1:1)', lebar: 150, tinggi: 150 },
  { kunci: 'kotak20', nama: 'Kotak 20 × 20 cm (1:1)', lebar: 200, tinggi: 200 },
  { kunci: 'a6', nama: 'A6 (10,5 × 14,8 cm)', lebar: 105, tinggi: 148 },
  { kunci: 'a5', nama: 'A5 (14,8 × 21 cm)', lebar: 148, tinggi: 210 },
  { kunci: 'a4', nama: 'A4 (21 × 29,7 cm)', lebar: 210, tinggi: 297 },
  { kunci: 'custom', nama: 'Ukuran sendiri (cm)', lebar: 100, tinggi: 100 },
];

export const MIN_CM = 3;
export const MAKS_CM = 100;

const batasCm = (v, bawaan) => {
  if (String(v ?? '').trim() === '') return bawaan;
  const n = Number(String(v).replace(',', '.'));
  if (!Number.isFinite(n)) return bawaan;
  return Math.min(MAKS_CM, Math.max(MIN_CM, n));
};

/** Ukuran kartu (mm) dari pilihan; ukuran sendiri dibatasi 3-100 cm dan boleh koma desimal. */
export const ukuranDariPilihan = (kunci, lebarCm = 10, tinggiCm = 10) => {
  const p = UKURAN_KARTU.find((u) => u.kunci === kunci) || UKURAN_KARTU[0];
  if (p.kunci !== 'custom') return { lebar: p.lebar, tinggi: p.tinggi };
  return { lebar: Math.round(batasCm(lebarCm, 10) * 100) / 10, tinggi: Math.round(batasCm(tinggiCm, 10) * 100) / 10 };
};

// 'google' = bingkai empat warna (merah atas, kuning kanan, hijau bawah, biru kiri) seperti kartu ulasan Google; lainnya satu warna.
export const AKSEN_GOOGLE = 'google';
export const WARNA_GOOGLE = { biru: '#4285f4', merah: '#ea4335', kuning: '#fbbc05', hijau: '#34a853' };
// Huruf "Google" berwarna (tulisan biasa, bukan berkas logo): G biru, o merah, o kuning, g biru, l hijau, e merah.
const HURUF_GOOGLE = [['G', WARNA_GOOGLE.biru], ['o', WARNA_GOOGLE.merah], ['o', WARNA_GOOGLE.kuning], ['g', WARNA_GOOGLE.biru], ['l', WARNA_GOOGLE.hijau], ['e', WARNA_GOOGLE.merah]];

export const WARNA_AKSEN = [
  {
    kunci: AKSEN_GOOGLE,
    nama: 'Google (empat warna)',
    latar: `conic-gradient(from -45deg, ${WARNA_GOOGLE.merah} 0 25%, ${WARNA_GOOGLE.kuning} 0 50%, ${WARNA_GOOGLE.hijau} 0 75%, ${WARNA_GOOGLE.biru} 0)`,
  },
  { kunci: '#1a73e8', nama: 'Biru' },
  { kunci: '#2e7d32', nama: 'Hijau' },
  { kunci: '#d32f2f', nama: 'Merah' },
  { kunci: '#ef6c00', nama: 'Oranye' },
  { kunci: '#6a1b9a', nama: 'Ungu' },
  { kunci: '#212121', nama: 'Hitam' },
];

export const TEKS_AJAKAN = 'Bantu kami dengan ulasan di';
export const TEKS_PEMINDAI = 'Scan QR';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const bintang = (cx, cy, r) => {
  const titik = [];
  for (let i = 0; i < 10; i += 1) {
    const rad = (i % 2 === 0 ? r : r * 0.4);
    const sudut = (-90 + i * 36) * (Math.PI / 180);
    titik.push(`${(cx + rad * Math.cos(sudut)).toFixed(2)},${(cy + rad * Math.sin(sudut)).toFixed(2)}`);
  }
  return `<polygon points="${titik.join(' ')}" fill="#fbbc04"/>`;
};

/** Memotong nama panjang jadi paling banyak dua baris (per kata), supaya tidak melebihi lebar kartu. */
export const bagiNama = (nama, maksHuruf = 22) => {
  const kata = String(nama || '').trim().split(/\s+/).filter(Boolean);
  const baris = [];
  let b = '';
  for (const k of kata) {
    if (!b) b = k;
    else if (b.length + 1 + k.length <= maksHuruf) b += ` ${k}`;
    else { baris.push(b); b = k; }
  }
  if (b) baris.push(b);
  if (baris.length > 2) return [baris[0], `${baris.slice(1).join(' ').slice(0, maksHuruf - 1)}…`];
  return baris;
};

/**
 * Rancangan kartu dalam satuan rancangan (lebar tetap 100 untuk kolom tunggal): daftar elemen dengan posisi, lalu
 * diskalakan seragam agar muat di kartu berukuran apa pun. Mengembalikan { w, h, unsur[] } (satuan rancangan).
 */
const rancang = ({ mendatar, adaLogo, barisNama, fN }) => {
  const unsur = [];
  const hNama = barisNama.length * fN * 1.25;
  if (!mendatar) {
    let y = 0;
    if (adaLogo) { unsur.push({ t: 'logo', cx: 50, cy: 11, r: 11 }); y = 25; }
    barisNama.forEach((b, i) => unsur.push({ t: 'nama', x: 50, y: y + fN * (i + 0.85) * 1.25, teks: b, f: fN }));
    y += hNama + 5;
    unsur.push({ t: 'ajakan', x: 50, y: y + 4.6, f: 5.2 });
    y += 8;
    unsur.push({ t: 'google', x: 50, y: y + 8.8, f: 11 });
    y += 13;
    unsur.push({ t: 'bintang', cx: 50, cy: y + 4.6, r: 4.6 });
    y += 13;
    unsur.push({ t: 'qr', x: 22, y, ukuran: 56 });
    y += 56 + 5;
    unsur.push({ t: 'scan', x: 50, y: y + 3.4, f: 4.6 });
    return { w: 100, h: y + 5, unsur };
  }
  // Mendatar: kolom kiri (logo, nama, ajakan, Google, bintang), kolom kanan (QR + Scan QR).
  let yk = 0;
  if (adaLogo) { unsur.push({ t: 'logo', cx: 25, cy: 11, r: 11 }); yk = 25; }
  barisNama.forEach((b, i) => unsur.push({ t: 'nama', x: 25, y: yk + fN * (i + 0.85) * 1.25, teks: b, f: fN }));
  yk += hNama + 5;
  unsur.push({ t: 'ajakan', x: 25, y: yk + 4.6, f: 4.2 });
  yk += 8;
  unsur.push({ t: 'google', x: 25, y: yk + 8.8, f: 11 });
  yk += 13;
  unsur.push({ t: 'bintang', cx: 25, cy: yk + 4.6, r: 4.6 });
  yk += 9.2;
  const tinggi = Math.max(yk, 56 + 5 + 5);
  const yq = (tinggi - (56 + 5 + 5)) / 2;
  unsur.push({ t: 'qr', x: 56, y: yq, ukuran: 56 });
  unsur.push({ t: 'scan', x: 84, y: yq + 56 + 5 + 3.4, f: 4.6 });
  return { w: 112, h: tinggi, unsur };
};

/**
 * Bingkai empat warna: empat sisi bersambung miring (45 derajat) di sudut, dipotong mengikuti sudut membulat kartu, lalu bagian
 * dalam diputihkan. Tiap sisi sedikit menumpang pada tetangganya supaya tidak ada garis tipis saat dirasterisasi.
 */
const bingkaiEmpatWarna = (W, H, b, radius) => {
  const e = b * 0.06;
  const n = (v) => Number(v.toFixed(3));
  const poli = (warna, titik) => `<polygon points="${titik.map(([x, y]) => `${n(x)},${n(y)}`).join(' ')}" fill="${warna}"/>`;
  const id = `klipBingkai${n(W)}x${n(H)}`.replace(/\./g, '_');
  const luar = radius + b / 2;
  const dalam = Math.max(radius - b / 2, 0);
  return `<defs><clipPath id="${id}"><rect x="0" y="0" width="${n(W)}" height="${n(H)}" rx="${n(luar)}"/></clipPath></defs>`
    + `<g clip-path="url(#${id})">`
    + poli(WARNA_GOOGLE.merah, [[0, 0], [W, 0], [W - b + e, b], [b - e, b]])
    + poli(WARNA_GOOGLE.kuning, [[W, 0], [W, H], [W - b, H - b + e], [W - b, b - e]])
    + poli(WARNA_GOOGLE.hijau, [[W, H], [0, H], [b - e, H - b], [W - b + e, H - b]])
    + poli(WARNA_GOOGLE.biru, [[0, H], [0, 0], [b, b - e], [b, H - b + e]])
    + '</g>'
    + `<rect x="${n(b)}" y="${n(b)}" width="${n(W - 2 * b)}" height="${n(H - 2 * b)}" rx="${n(dalam)}" fill="#ffffff"/>`;
};

/**
 * SVG kartu ulasan (satuan mm). logoDataUrl: logo cabang sebagai data URL (kosong = tanpa logo, nama cabang lebih besar).
 * aksen: 'google' (bawaan) = bingkai empat warna; atau satu warna (#rrggbb). Huruf "Google" berwarna digambar sebagai teks.
 */
export const bangunSvgKartu = ({ lebar, tinggi, namaCabang = '', logoDataUrl = '', urlUlasan, aksen = AKSEN_GOOGLE }) => {
  const W = Number(lebar);
  const H = Number(tinggi);
  const s = Math.min(W, H);
  const mendatar = W > H * 1.25;
  const bingkai = s * 0.035;
  const radius = s * 0.05;
  const pad = s * 0.075 + bingkai;
  // Ukuran huruf nama; panjang baris mengikuti lebar kolom rancangan (kolom tunggal 96, dua kolom 48) dibagi lebar rata-rata huruf.
  const fN = mendatar ? (logoDataUrl ? 4.4 : 5.4) : (logoDataUrl ? 5.4 : 7.2);
  const barisNama = bagiNama(namaCabang, Math.floor((mendatar ? 48 : 96) / (fN * 0.56)));
  const r = rancang({ mendatar, adaLogo: !!logoDataUrl, barisNama, fN });
  const skala = Math.min((W - 2 * pad) / r.w, (H - 2 * pad) / r.h);
  const tx = (W - r.w * skala) / 2;
  const ty = (H - r.h * skala) / 2;
  const huruf = 'font-family="Helvetica, Arial, sans-serif"';
  let isi = '';
  for (const u of r.unsur) {
    if (u.t === 'logo') {
      isi += `<clipPath id="klipLogo"><circle cx="${u.cx}" cy="${u.cy}" r="${u.r}"/></clipPath>`
        + `<image href="${esc(logoDataUrl)}" x="${u.cx - u.r}" y="${u.cy - u.r}" width="${u.r * 2}" height="${u.r * 2}" preserveAspectRatio="xMidYMid slice" clip-path="url(#klipLogo)"/>`
        + `<circle cx="${u.cx}" cy="${u.cy}" r="${u.r}" fill="none" stroke="#dddddd" stroke-width="0.6"/>`;
    } else if (u.t === 'nama') {
      isi += `<text x="${u.x}" y="${u.y.toFixed(2)}" font-size="${u.f}" font-weight="700" text-anchor="middle" fill="#212121" ${huruf}>${esc(u.teks)}</text>`;
    } else if (u.t === 'ajakan') {
      isi += `<text x="${u.x}" y="${u.y.toFixed(2)}" font-size="${u.f}" text-anchor="middle" fill="#424242" ${huruf}>${esc(TEKS_AJAKAN)}</text>`;
    } else if (u.t === 'google') {
      isi += `<text x="${u.x}" y="${u.y.toFixed(2)}" font-size="${u.f}" font-weight="700" text-anchor="middle" ${huruf}>`
        + HURUF_GOOGLE.map(([h, w]) => `<tspan fill="${w}">${h}</tspan>`).join('')
        + '</text>';
    } else if (u.t === 'bintang') {
      for (let i = 0; i < 5; i += 1) isi += bintang(u.cx + (i - 2) * u.r * 2.35, u.cy, u.r);
    } else if (u.t === 'qr') {
      isi += `<rect x="${u.x - 2}" y="${u.y - 2}" width="${u.ukuran + 4}" height="${u.ukuran + 4}" rx="3" fill="#ffffff" stroke="#dddddd" stroke-width="0.5"/>`
        + svgQr(urlUlasan).replace('<svg ', `<svg x="${u.x}" y="${u.y}" width="${u.ukuran}" height="${u.ukuran}" `);
    } else if (u.t === 'scan') {
      isi += `<text x="${u.x}" y="${u.y.toFixed(2)}" font-size="${u.f}" font-weight="700" text-anchor="middle" fill="#424242" ${huruf}>${esc(TEKS_PEMINDAI)}</text>`;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}mm" height="${H}mm" viewBox="0 0 ${W} ${H}">`
    + (aksen === AKSEN_GOOGLE
      ? bingkaiEmpatWarna(W, H, bingkai, radius)
      : `<rect x="${bingkai / 2}" y="${bingkai / 2}" width="${W - bingkai}" height="${H - bingkai}" rx="${radius}" fill="#ffffff" stroke="${esc(aksen)}" stroke-width="${bingkai}"/>`)
    + `<g transform="translate(${tx.toFixed(3)} ${ty.toFixed(3)}) scale(${skala.toFixed(5)})">${isi}</g></svg>`;
};

/** Dokumen HTML untuk dicetak: satu halaman seukuran kartu, tanpa margin. */
export const htmlKartu = (svg, lebar, tinggi) => `<!doctype html><html><head><meta charset="utf-8"><title>Kartu QR Ulasan</title><style>
  @page { size: ${lebar}mm ${tinggi}mm; margin: 0 }
  html, body { margin: 0; padding: 0 }
  svg { display: block; width: ${lebar}mm; height: ${tinggi}mm }
</style></head><body>${svg}</body></html>`;

/** Nama berkas aman dari nama cabang. */
export const namaBerkasKartu = (namaCabang, ekstensi) => `kartu-ulasan-${String(namaCabang || 'cabang').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'cabang'}.${ekstensi}`;
